import { useEffect, useState } from "react";
import { http, fmtDate, fmtUptime } from "@/lib/api";
import { ArrowClockwise, Broadcast } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import KickDialog from "@/components/KickDialog";

const eventColor = (e) => {
  if (e.includes("ACCEPT")) return "text-[hsl(var(--success))]";
  if (e.includes("REJECT")) return "text-[hsl(var(--destructive))]";
  if (e.includes("STOP")) return "text-muted-foreground";
  return "text-primary";
};

export default function RadiusLogs() {
  const [sessions, setSessions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [nasList, setNasList] = useState([]);
  const [nasFilter, setNasFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = nasFilter === "all" ? "" : `?nas_id=${nasFilter}`;
    const [a, b, n] = await Promise.all([
      http.get(`/radius/sessions${params}`),
      http.get(`/radius/logs${params}`),
      http.get("/nas"),
    ]);
    setSessions(a.data); setLogs(b.data); setNasList(n.data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [nasFilter]);

  const kick = async () => { /* replaced by <KickDialog /> — kept as no-op to avoid churning downstream refs */ };

  return (
    <div className="space-y-6 fade-up" data-testid="radius-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">RADIUS accounting</div>
          <h1 className="font-display font-bold text-4xl mt-1">Live sessions & logs</h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={nasFilter} onValueChange={setNasFilter}>
            <SelectTrigger className="w-56" data-testid="radius-nas-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All NAS devices</SelectItem>
              {nasList.map((n) => <SelectItem key={n.id} value={n.id}>{n.name} · {n.nas_ip}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="rounded-sm border-border" data-testid="radius-refresh">
            <ArrowClockwise size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div>
        <div className="uppercase-label mb-2">Active sessions · {sessions.length}</div>
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border">
              <th className="uppercase-label text-left py-2 px-3">Session</th>
              <th className="uppercase-label text-left py-2 px-3">User</th>
              <th className="uppercase-label text-left py-2 px-3">IP</th>
              <th className="uppercase-label text-left py-2 px-3">MAC</th>
              <th className="uppercase-label text-left py-2 px-3">NAS</th>
              <th className="uppercase-label text-left py-2 px-3">Uptime</th>
              <th className="uppercase-label text-right py-2 px-3">In / Out</th>
              <th className="uppercase-label text-right py-2 px-3">CoA</th>
            </tr></thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.session_id} className="border-b border-border/60 hover:bg-accent/30" data-testid={`session-${s.session_id}`}>
                  <td className="py-2 px-3 data-cell">{s.session_id}</td>
                  <td className="py-2 px-3">{s.full_name} <span className="text-muted-foreground font-mono text-xs">· {s.username}</span></td>
                  <td className="py-2 px-3 data-cell">{s.ip_address}</td>
                  <td className="py-2 px-3 data-cell text-muted-foreground">{s.mac_address}</td>
                  <td className="py-2 px-3 text-sm">
                    <div>{s.nas_name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{s.nas_ip}</div>
                  </td>
                  <td className="py-2 px-3 data-cell">{fmtUptime(s.uptime_sec)}</td>
                  <td className="py-2 px-3 data-cell text-right">{s.in_mb} / {s.out_mb} MB</td>
                  <td className="py-2 px-3 text-right">
                    <KickDialog subscriberId={s.subscriber_id} username={s.username} onDone={load}>
                      <button
                        className="text-[10px] font-mono uppercase tracking-wide px-2 py-1 border border-[hsl(var(--destructive)/0.4)] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive)/0.15)] transition-colors rounded-sm inline-flex items-center gap-1"
                        data-testid={`kick-${s.session_id}`}
                        title="Kick (RADIUS Disconnect) with optional re-auth block"
                      >
                        <Broadcast size={10} /> Kick
                      </button>
                    </KickDialog>
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground text-sm">No active sessions.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="uppercase-label mb-2">Recent events</div>
        <div className="border border-border bg-card">
          <div className="max-h-[420px] overflow-y-auto font-mono text-xs">
            {logs.map((l, i) => (
              <div key={i} className="border-b border-border/40 py-1.5 px-3 flex gap-3 hover:bg-accent/20" data-testid={`log-${i}`}>
                <span className="text-muted-foreground shrink-0 w-32">{fmtDate(l.ts)}</span>
                <span className={`shrink-0 w-32 ${eventColor(l.event)}`}>{l.event}</span>
                <span className="shrink-0 w-28 text-muted-foreground truncate">{l.username}</span>
                <span className="shrink-0 w-32 text-primary/80 truncate">{l.nas_name}</span>
                <span className="truncate">{l.message}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
